import type { Maybe } from '@adornis/base/utilTypes.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { context } from '@adornis/baseql/server/context.js';
import { accessController } from './abac.js';
import type { SaraaScopeResult } from './policies/defineSaraaScopePolicy.js';

/**
 * Guards a single baseql operation with one scope action.
 *
 * Permissions belong on the endpoint, not in a table somewhere else that has to
 * be kept in sync with it — so every custom mutation, query and subscription
 * carries its own guard:
 *
 * ```ts
 * registerMutation({
 *   operationName: 'updateOrganization',
 *   type: () => Boolean,
 *   params: [{ name: 'organization', type: () => Organization }],
 *   middlewares: [abacGuard(Organizations, 'modify', (args: { organization: Organization }) =>
 *     loadOrganizationTarget(args.organization._id))],
 *   resolve: resolveUpdateOrganization,
 * });
 * ```
 *
 * Three things make this safe to write in an isomorphic `api/` file:
 *
 *  - `registerOperation` drops the whole `middlewares` array on the client, so
 *    the body only ever runs on the server. A `resolveTarget` imported from a
 *    `server/` folder is `undefined` in the browser bundle and is never called
 *    there — the same arrangement the resolvers themselves already use.
 *  - `upgradeMiddleware` is always prepended, so `args` carries hydrated entity
 *    instances rather than the plain objects that came off the wire.
 *  - `ServerContextPolicy` is first in the controller, so migrations, seeds and
 *    queue workers — anything running without a request — pass untouched.
 */
export const abacGuard =
  <
    A extends readonly string[],
    D,
    U extends { _id: string },
    Args extends Record<string, unknown> = Record<string, unknown>,
  >(
    scope: SaraaScopeResult<A, D, U>,
    action: A[number],
    /**
     * The data the scope's checks decide against — an organization id, the
     * document being written, the target user.
     *
     * **Load it from the database, never take it from the caller.** The whole
     * point of the check is to establish authority over what is *there*; reading
     * the organization off an incoming entity would let a caller name any
     * organization they like and be granted it.
     *
     * Omit it for actions that need no target (a coarse "may this user use this
     * endpoint at all"), and let the resolver make the per-row decision.
     */
    resolveTarget?: (args: Args) => Maybe<D> | Promise<Maybe<D>>,
  ): BaseQLMiddleware =>
  async ({ args, next }) => {
    if (context.serverContext) return next();
    const data = await resolveTarget?.(args as Args);
    await accessController.assert(scope.createPayload({ action, data: data ?? undefined }) as never);
    return next();
  };
